import React from 'react';
import { Link } from 'react-router-dom';
import CompanyDetailsBlock from '../components/CompanyDetailsBlock';

const Section = ({ title, children }) => (
  <section>
    <h2
      className="text-xl font-semibold text-[#112318] mb-3"
      style={{ fontFamily: 'Playfair Display, serif' }}
    >
      {title}
    </h2>
    <div className="leading-relaxed space-y-3">{children}</div>
  </section>
);

const OrganiserTermsPage = () => (
  <div className="min-h-screen bg-[#FAFAF7]">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="organiser-terms-page">
      <h1
        className="text-4xl sm:text-5xl tracking-tight leading-none font-bold text-[#112318] mb-4"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        Organiser Terms
      </h1>
      <p className="text-sm text-[#4A5D50] mb-12">Effective Date: May 2026</p>

      <div className="prose prose-sm max-w-none space-y-10 text-[#4A5D50]">
        <Section title="1. Organiser Account Creation">
          <p>
            To list events on VinePass, you must create an organiser account and verify your email
            address. You agree to provide accurate organisation details, including a valid contact
            email, organisation name and (when requesting payouts) banking information for a South
            African bank account in the name of your organisation or its accountable individual.
          </p>
        </Section>

        <Section title="2. Event Listing Responsibilities">
          <p>
            Organisers are responsible for the accuracy, lawfulness and clarity of every event
            listing they publish on VinePass. Event titles, descriptions, dates, locations, age
            restrictions and ticket pricing must reflect the experience actually being offered.
          </p>
        </Section>

        <Section title="3. Accurate Event Information">
          <p>
            Any material change to an event after it has been published — including date, time,
            venue, line-up or pricing — must be updated on VinePass without undue delay. Where a
            change materially affects ticket holders, organisers are responsible for notifying
            attendees and, where appropriate, processing refunds in accordance with the Refund
            Policy.
          </p>
        </Section>

        <Section title="4. Ticket Pricing">
          <p>
            Organisers set their own ticket prices. VinePass applies a transparent service fee per
            ticket; the fee structure is disclosed on the <Link to="/fees" className="text-[#1F3D2B] underline">Fees</Link> page and selectable
            per event (organiser-absorbed, customer-paid, or split). Ticket prices must include all
            applicable taxes where required by South African law.
          </p>
        </Section>

        <Section title="5. Guest Check-in and Scanning">
          <p>
            Organisers and their authorised associates are responsible for validating QR-coded
            tickets at the door using the VinePass scanner. You must not knowingly admit holders of
            cancelled or refunded tickets and you must ensure your team respects guest privacy
            during the check-in process.
          </p>
        </Section>

        <Section title="6. Refunds and Cancellations">
          <p>
            Organisers carry primary responsibility for refunding ticket holders when an event is
            cancelled or materially changed. VinePass facilitates the refund mechanism on your
            behalf via PayFast, but the obligation to refund rests with the organiser. See the{' '}
            <Link to="/refund-policy" className="text-[#1F3D2B] underline">Refund Policy</Link> for
            details.
          </p>
        </Section>

        <Section title="7. Payout Eligibility">
          <p>
            Payouts are calculated on completed ticket orders for events that have taken place,
            net of approved refunds, chargebacks and reserve holds. Payouts require verified
            banking details on file. See the <Link to="/payout-policy" className="text-[#1F3D2B] underline">Payout Policy</Link> for the
            review and approval process.
          </p>
        </Section>

        <Section title="8. Prohibited Events and Misleading Listings">
          <p>
            VinePass does not allow events that are illegal under South African law, that
            misrepresent the experience offered, or that are designed to deceive ticket buyers.
            Listings containing unlawful, hateful, fraudulent or grossly misleading content will
            be removed.
          </p>
        </Section>

        <Section title="9. VinePass Right to Review, Suspend, or Remove">
          <p>
            VinePass may review any event listing or organiser account at any time and reserves
            the right to suspend, remove or restrict access to events or accounts that, in our
            reasonable opinion, breach these Organiser Terms, the platform Terms &amp; Conditions,
            or applicable law. Where possible, we will notify the organiser before taking action.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            For organiser support, email{' '}
            <a href="mailto:info@vinepass.co.za" className="text-[#1F3D2B] underline">info@vinepass.co.za</a>.
          </p>
        </Section>

        <CompanyDetailsBlock />
      </div>

      <div className="mt-16 pt-8 border-t border-[#E5E9E6] flex items-center justify-between">
        <Link to="/" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="organiser-terms-back-home">
          &larr; Back to Home
        </Link>
        <Link to="/payout-policy" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="organiser-terms-to-payout">
          Payout Policy &rarr;
        </Link>
      </div>
    </div>
  </div>
);

export default OrganiserTermsPage;
